import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Checkbox,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { OptionNumberField } from '../commitment-fields';
import { PriceRangesList } from '../price-ranges-list';
import { useServicePriceTotal } from '../use-service-price-total';
import { TemplateSection, TemplateStack } from '../template-shell';
import { TemplatePanel } from '../template-panel';
import { StatementToggle } from '../statement-toggle';
import { isStatementable } from '../statementable-templates';
import { readProductConfigOptions } from '../product-config-option';
import type { ProductTemplateProps } from '../template-props';
import type { ContractFormValues } from '../../contract-form';

/**
 * Unit options for Transcoding/Recording/Transmuxing options — mirrors
 * PRODUCT_TRANSCODING_UNITS from the legacy constants.
 */
const UNIT_OPTIONS = [
  { value: 'hour', label: 'Hour' },
  { value: 'stream', label: 'Stream' },
];

const PRICE_METHOD_OPTIONS = [
  { value: 'tierd', label: 'Tiered' },
  { value: 'progressive', label: 'Progressive' },
];

/**
 * SwiftFederation.Transcoding template (also Recording / StorageOSS / StorageLFD
 * / Transmuxing — same builder). Seeds one entry per product_config_options item,
 * then renders per-option cards with unit, commitment (when commit form),
 * price method, and price ranges.
 */
export function TranscodingTemplate({ index, templateName, detail }: ProductTemplateProps) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = useWatch({ control, name: optsPath }) ?? [];
  // Watch product-level price model to gate commitment fields.
  const priceModel = useWatch({ control, name: `products.${index}.service_price_model` });
  const isCommit = priceModel === 'commit';

  // Derive register price from Σ(commitment × unit price) so a prepaid commitment
  // shows in the contract Total (matches legacy Transcoding's Bundle price total).
  useServicePriceTotal(index);

  const configOptions = readProductConfigOptions(detail);
  const seededFor = useRef<string>('');

  // Seed service_configoptions from the product catalog once (guard via ref to
  // survive React Strict Mode double-invoke without duplicating rows).
  useEffect(() => {
    const key = `${index}:${configOptions.map((o) => o.name).join(',')}`;
    if (!configOptions.length || seededFor.current === key) return;
    const current = getValues(optsPath);
    if (Array.isArray(current) && current.length > 0 && seededFor.current !== '') return;
    seededFor.current = key;

    setValue(
      optsPath,
      configOptions.map((opt) => {
        // Preserve any already-entered values on re-mount (e.g. product swap).
        const existing = Array.isArray(current)
          ? current.find((s) => s.name === opt.name)
          : undefined;

        const prices = (opt.product_pricing_steps ?? []).length > 0
          ? opt.product_pricing_steps!.map((s) => ({
              price_start: s.price_start,
              price_end: s.price_end,
              price_per_unit: s.price_per_unit,
            }))
          : [{ price_start: 0, price_end: 999999999, price_per_unit: 0 }];

        return {
          name: opt.name,
          unit: opt.unit ?? 'hour',
          type: opt.type ?? 'transcoding',
          price_config: { method: 'progressive' },
          commitment_prepaid: true,
          // Default on: SwiftFederation services participate in monthly usage
          // reconciliation (đối soát); the visible StatementToggle lets the
          // salesperson opt a specific config OUT.
          statementable: true,
          commitment_value: 0,
          commitment_price_per_unit: 0,
          commitment_time: 1,
          prices,
          ...(existing ?? {}),
        };
      }) as never,
      { shouldDirty: true },
    );
  }, [configOptions, index, optsPath, getValues, setValue]);

  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <TemplatePanel title="Transcoding configuration">
      <TemplateStack>
        {(options as unknown[]).map((_, i) => (
          <TranscodingOptionCard
            key={i}
            index={index}
            optionIndex={i}
            isCommit={isCommit}
            templateName={templateName}
          />
        ))}
      </TemplateStack>
    </TemplatePanel>
  );
}

interface CardProps {
  index: number;
  optionIndex: number;
  isCommit: boolean;
  templateName: string;
}

function TranscodingOptionCard({ index, optionIndex, isCommit, templateName }: CardProps) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const optPath = `products.${index}.service_configoptions.${optionIndex}` as const;
  const name = useWatch({ control, name: `${optPath}.name` as never }) as string | undefined;
  const commitmentValue = Number(
    useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0,
  );

  return (
    <TemplateSection title={(name ?? 'Option').toUpperCase()}>
      {/* Unit is fixed by the product catalog; the user cannot change it. */}
      <Controller
        control={control}
        name={`${optPath}.unit` as never}
        render={({ field }) => (
          <Field label="Unit of calculation">
            {(p) => (
              <Select value={(field.value as string) || 'hour'} onValueChange={field.onChange} disabled>
                <SelectTrigger {...p} onBlur={field.onBlur}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
      />

      {/* Commitment block — only visible when the product form is 'commit'. */}
      {isCommit && (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <OptionNumberField path={`${optPath}.commitment_value`} label="Commitment value" />
          <OptionNumberField path={`${optPath}.commitment_time`} label="Commitment time (months)" />
          <OptionNumberField path={`${optPath}.commitment_price_per_unit`} label="Price per unit" />
          <Controller
            control={control}
            name={`${optPath}.commitment_prepaid` as never}
            render={({ field }) => (
              <Field label="Prepaid">
                {() => (
                  <Checkbox
                    checked={field.value === true}
                    onCheckedChange={(v) =>
                      setValue(
                        `${optPath}.commitment_prepaid` as never,
                        (v === true) as never,
                        { shouldDirty: true },
                      )
                    }
                  />
                )}
              </Field>
            )}
          />
        </div>
      )}

      {/* Price method and ranges apply regardless of commitment form. */}
      <Controller
        control={control}
        name={`${optPath}.price_config.method` as never}
        render={({ field }) => (
          <Field label="Form of comparison">
            {(p) => (
              <Select value={(field.value as string) || 'progressive'} onValueChange={field.onChange}>
                <SelectTrigger {...p} onBlur={field.onBlur}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_METHOD_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
      />

      <PriceRangesList name={`${optPath}.prices`} commitmentValue={commitmentValue} />

      {/* Statement toggle: only rendered for templates where statementable has billing
          effect (isStatementable guard). Default is on; salesperson may opt config OUT. */}
      {isStatementable(templateName) && (
        <StatementToggle optPath={`products.${index}.service_configoptions.${optionIndex}`} />
      )}
    </TemplateSection>
  );
}
